import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AxiosError } from 'axios';
import {
  ApplicationApi,
  type ApplicationPayload,
  type ApplicationResponse,
  type ApplicationStatusResponse,
} from '../services';
import { normalizePhoneNumber } from '../utils/phone';

export type ApplicationInput = {
  fullName: string;
  phone: string;
  idNumber: string;
  email?: string | null;
  reason: string;
};

type ApplicationContextValue = {
  submitting: boolean;
  error: string | null;
  lastSubmission: ApplicationResponse | null;
  lastSubmittedPhone: string | null;
  submitApplication: (application: ApplicationInput) => Promise<ApplicationResponse>;
  checkStatus: (phone: string) => Promise<ApplicationStatusResponse>;
  clearError: () => void;
  resetSubmission: () => void;
};

const ApplicationContext = createContext<ApplicationContextValue | undefined>(undefined);

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    return data?.error ?? data?.message ?? error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong. Please try again.';
};

export const ApplicationProvider = ({ children }: { children: ReactNode }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSubmission, setLastSubmission] = useState<ApplicationResponse | null>(null);
  const [lastSubmittedPhone, setLastSubmittedPhone] = useState<string | null>(null);

  const buildPayload = (application: ApplicationInput): ApplicationPayload => {
    const payload: ApplicationPayload = {
      full_name: application.fullName.trim(),
      phone: normalizePhoneNumber(application.phone.trim()),
      id_number: application.idNumber.trim(),
      reason_for_joining: application.reason.trim(),
    };

    const email = application.email?.trim();
    if (email) {
      payload.email = email;
    }

    if (!payload.reason_for_joining) {
      delete payload.reason_for_joining;
    }

    return payload;
  };

  const submitApplication = useCallback(async (application: ApplicationInput) => {
    setSubmitting(true);
    setError(null);

    try {
      const payload = buildPayload(application);
      const response = await ApplicationApi.submitApplication(payload);
      setLastSubmission(response);
      setLastSubmittedPhone(payload.phone);
      return response;
    } catch (submissionError) {
      const message = parseErrorMessage(submissionError);
      setError(message);
      throw new Error(message);
    } finally {
      setSubmitting(false);
    }
  }, []);

  const checkStatus = useCallback(async (phone: string) => {
    setError(null);
    try {
      const normalizedPhone = normalizePhoneNumber(phone.trim());
      return await ApplicationApi.checkStatus(normalizedPhone);
    } catch (statusError) {
      const message = parseErrorMessage(statusError);
      setError(message);
      throw new Error(message);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  const resetSubmission = useCallback(() => {
    setLastSubmission(null);
    setLastSubmittedPhone(null);
    setError(null);
  }, []);

  const value = useMemo<ApplicationContextValue>(
    () => ({
      submitting,
      error,
      lastSubmission,
      lastSubmittedPhone,
      submitApplication,
      checkStatus,
      clearError,
      resetSubmission,
    }),
    [submitting, error, lastSubmission, lastSubmittedPhone, submitApplication, checkStatus, clearError, resetSubmission],
  );

  return <ApplicationContext.Provider value={value}>{children}</ApplicationContext.Provider>;
};

export const useApplication = (): ApplicationContextValue => {
  const context = useContext(ApplicationContext);

  if (!context) {
    throw new Error('useApplication must be used within an ApplicationProvider');
  }

  return context;
};

